export interface OrgLevelNode {
    id: string;
    name: string;
    parentId?: string;
}

export function findLowestCommonAncestor(orgLevelIds: string[], orgLevels: OrgLevelNode[]): { id: string; name: string } {
    if (!orgLevelIds.length) {
        return { id: '', name: '' };
    }

    const idMap = new Map<string, OrgLevelNode>(orgLevels.map(o => [o.id, o]));

    const walkAncestors = (id: string): string[] => {
        const chain: string[] = [];
        const visited = new Set<string>();
        let current: OrgLevelNode | undefined = idMap.get(id);
        while (current && !visited.has(current.id)) {
            visited.add(current.id);
            chain.push(current.id);
            current = current.parentId != null ? idMap.get(current.parentId) : undefined;
        }
        return chain;
    };

    const uniqueIds = [...new Set(orgLevelIds)];

    if (uniqueIds.length === 1) {
        const node = idMap.get(uniqueIds[0]);
        if (!node) {
            return { id: uniqueIds[0], name: '' };
        }
        return { id: node.id, name: node.name };
    }

    const chains = uniqueIds.map(id => walkAncestors(id));
    const common = chains.slice(1).reduce<string[]>(
        (acc, chain) => {
            const chainSet = new Set(chain);
            return acc.filter(id => chainSet.has(id));
        },
        chains[0]
    );

    if (!common.length) {
        const fallbackId = orgLevelIds[0];
        const fallback = idMap.get(fallbackId);
        return { id: fallbackId, name: fallback?.name ?? '' };
    }

    const lcaId = common[0];
    const lcaNode = idMap.get(lcaId)!;
    return { id: lcaNode.id, name: lcaNode.name };
}
